// Fits a page's meta description into the network standard: 120 to 160
// characters, ending on a full sentence, never cut mid-phrase.
//
// Used by Seo.tsx at runtime and by scripts/prerender.mts at build time so
// the description in raw HTML and after hydration is identical.
//
// Strategy, in order:
//   1. Keep as many of the page's own sentences as fit in 160 characters.
//   2. If the result is under 120, append short factual tails (each a full
//      sentence) until it reaches 120 without passing 160.
//   3. If even the first sentence is longer than 160, end it at the last
//      clause boundary (comma, colon, or semicolon) that fits, close it with
//      a period, then apply step 2.

export const DESCRIPTION_MIN = 120;
export const DESCRIPTION_MAX = 160;

const TAILS = [
  "Reviewed by a board-certified psychiatrist.",
  "Plain-language education, not medical advice.",
  "Part of The Shrink Network.",
  "From AnxietyResource.",
];

function sentences(text: string): string[] {
  const clean = text.replace(/\s+/g, " ").trim();
  if (!clean) return [];
  // Split after . ! ? when followed by a space and an uppercase letter or
  // digit. Keeps abbreviations like "e.g." and "MD, MBA." mostly intact.
  const parts = clean.split(/(?<=[.!?])\s+(?=[A-Z0-9"(])/);
  return parts.map((p) => p.trim()).filter(Boolean);
}

function closeSentence(s: string): string {
  const t = s.trim().replace(/[,;:\s]+$/, "");
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

function clauseTrim(s: string, max: number): string {
  const slice = s.slice(0, max);
  const idx = Math.max(
    slice.lastIndexOf(", "),
    slice.lastIndexOf(": "),
    slice.lastIndexOf("; "),
  );
  if (idx >= 40) return closeSentence(slice.slice(0, idx));
  // No clause boundary: fall back to the last whole word.
  const w = slice.lastIndexOf(" ");
  return closeSentence(slice.slice(0, w > 40 ? w : max - 1));
}

export function fitDescription(input: string): string {
  const raw = (input ?? "").replace(/\s+/g, " ").trim();
  if (raw.length >= DESCRIPTION_MIN && raw.length <= DESCRIPTION_MAX && /[.!?]$/.test(raw)) {
    return raw;
  }
  const parts = sentences(raw);
  let out = "";
  for (const p of parts) {
    const next = out ? `${out} ${p}` : p;
    if (next.length > DESCRIPTION_MAX) break;
    out = next;
  }
  if (!out) {
    // First sentence alone is too long.
    out = clauseTrim(parts[0] ?? raw, DESCRIPTION_MAX - 1);
  }
  out = closeSentence(out);
  for (const tail of TAILS) {
    if (out.length >= DESCRIPTION_MIN) break;
    if (out.includes(tail.replace(/\.$/, ""))) continue;
    const next = `${out} ${tail}`;
    if (next.length <= DESCRIPTION_MAX) out = next;
  }
  return out;
}
